import styled from "styled-components";
import { Link } from "react-router-dom";
import colours from "../../lib/colours";
import { ReactComponent as ArrowIcon } from "../../assets/svg/arrow-right-long.svg";
import Footer from "../Other/Footer";
import appConfig from "../../lib/appConfig";
import Dropdown from "../../components/atoms/Dropdown";
import { TextInput } from "../../components/atoms/TextInput";
import { useCallback, useEffect, useState } from "react";
import FilledButton from "../../components/atoms/FilledButton";
import SpinnerBase from "../../components/atoms/SpinnerBase";
import TextButton from "../../components/atoms/TextButton";
import useInterval from "../../utils/useInterval";
import { processingStatuses } from "../../lib/types";
import {
  checkAdminExportStatus,
  downloadAdminExport,
  requestAdminExport,
} from "../../utils/requests";

const Container = styled.div`
  height: calc(100vh - ${appConfig.headerHeight}px);
  display: flex;
  flex-direction: column;
  font-weight: 300;
  color: ${colours.darkerGrey};
`;

const SubHeader = styled.div`
  background-color: ${colours.th1l1alt};
  color: ${colours.white};
`;

const FluidContainer = styled.div`
  max-width: ${appConfig.homePageContentWidth};
  margin: auto;
  padding-inline: 1rem;
`;

const SubHeaderInnerContainer = styled(FluidContainer)`
  padding-block: 1rem;
`;

const SubHeaderLinkContainer = styled.div`
  height: 69px;
  box-sizing: border-box;
  padding-top: 8px;
`;

const SubHeaderLinkInnerContainer = styled.div`
  display: flex;
  align-items: center;
`;

const BackLink = styled(Link)`
  padding: 2px 0;
  font-weight: 400;
  font-size: 14px;
  color: ${colours.white};
  display: inline;
  text-decoration: none;
  &: hover {
    text-decoration: underline;
  }
`;

const StyledBackArrow = styled(ArrowIcon)`
  position: relative;
  top: -2px;
  width: 1.6875rem;
  height: 0.875rem;
  transform: rotate(180deg);
  margin-right: 12px;
`;

const SubHeaderText = styled.h2`
  font-size: 2.1875rem;
  font-weight: 500;
  margin-block: -2px 4px;
`;

const MainContent = styled.div`
  flex: 1;
`;

const MainContentInner = styled(FluidContainer)`
  margin-block: 1.8rem;
  padding-bottom: 80px;
  font-weight: 400;
  color: ${colours.th1};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  & + & {
    margin-block-start: 24px;
  }
`;

const FormLabel = styled.label`
  margin-bottom: 6px;
  cursor: pointer;
`;

const StyledInput = styled(TextInput)`
  width: 192px;
`;

const StyledDropdown = styled(Dropdown)`
  width: 268px;
`;

const StyledCheckboxContainer = styled.div`
  & + & {
    margin-block-start: 12px;
  }
`;

const StyledCheckbox = styled.input`
  cursor: pointer;
  vertical-align: middle;
  width: 18px;
  height: 18px;
  margin-inline: 0;
`;

const CheckboxLabel = styled.label`
  margin-bottom: 6px;
  padding-left: 10px;
  cursor: pointer;
`;

const FooterButtonContainer = styled.div`
  margin-block: 24px;
  display: flex;
  justify-content: center;
`;

const FooterButton = styled(FilledButton)`
  display: flex;
  align-items: center;
  height: auto;
  padding: 14px 40px;
  min-width: min(100%, 320px);
  justify-content: center;
`;

const DownloadErrorDiv = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colours.errord1};
  font-weight: 500;
  flex-wrap: wrap;
`;

const TryAgainButton = styled(TextButton)`
  font-size: 16px;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  margin-left: 4px;
`;

const spinnerStyle = {
  display: "flex",
  alignItems: "center",
  marginLeft: "6px",
  width: "auto",
};

const spinnerChildrenStyle = {
  width: "8px",
  height: "8px",
};

const pollInterval = 2000;

function Admin() {
  const exportTypeDescriptions = [
    "Export features (shapefiles)",
    "Export CSV files for NVCR",
  ];
  const [reportNumber, setReportNumber] = useState<string>("");
  const [exportTypeValue, setExportTypeValue] = useState(
    exportTypeDescriptions[0]
  );
  const [speciesSummaryChecked, setSpeciesSummaryChecked] = useState(true);
  const [zoneSummaryChecked, setZoneSummaryChecked] = useState(false);
  const [exportId, setExportId] = useState<string>();
  const [processingStatus, setProcessingStatus] =
    useState<processingStatuses>();
  const [preparingExport, setPreparingExport] = useState(false);
  const [exportError, setExportError] = useState(false);
  const { start: startPolling, stop: stopPolling } = useInterval(() => {
    pollReport();
  }, pollInterval);

  const abortPolling = useCallback(() => {
    stopPolling();
    setPreparingExport(false);
  }, [stopPolling]);

  const pollReport = useCallback(async () => {
    if (
      !exportId ||
      ![processingStatuses.processing, undefined].includes(processingStatus)
    )
      return;
    checkAdminExportStatus(exportId)
      .then((response: any) => {
        setProcessingStatus(response.processingStatus);
        if (response.processingStatus === processingStatuses.completed) {
          stopPolling();
        }
        if (response.processingStatus === processingStatuses.failed) {
          setExportError(true);
          abortPolling();
        }
      })
      .catch((e: any) => {
        console.error(e);
        setExportError(true);
        abortPolling();
      });
  }, [exportId, processingStatus, abortPolling, stopPolling]);

  // handle completed/failed report
  useEffect(() => {
    if (!exportId) return;
    const handleError = () => {
      setExportError(true);
      setExportId(undefined);
      setProcessingStatus(undefined);
    };
    if (processingStatus === processingStatuses.completed) {
      setTimeout(() => {
        downloadAdminExport(exportId)
          .then(() => {
            setExportId(undefined);
            setProcessingStatus(undefined);
            setPreparingExport(false);
          })
          .catch((e: any) => {
            console.error(e);
            handleError();
          });
      }, 4000);
    }
    if (processingStatus === processingStatuses.failed) {
      handleError();
    }
  }, [exportId, processingStatus]);

  const determineExportType = () => {
    if (exportTypeValue === exportTypeDescriptions[1]) {
      if (!zoneSummaryChecked) {
        return "nvcrSpecies";
      } else if (!speciesSummaryChecked) {
        return "nvcrZone";
      } else {
        return "nvcrSpeciesZone";
      }
    }
    return "shp";
  };

  const requestExport = () => {
    setPreparingExport(true);
    setExportError(false);

    const exportType = determineExportType();

    const handleError = (e: any) => {
      console.error(e);
      setExportError(true);
      abortPolling();
    };
    requestAdminExport(reportNumber, exportType)
      .then((response: any) => {
        setExportId(response.exportJobId);
        startPolling();
      })
      .catch(handleError);
  };

  const onReportNumberChange = (e: any) => {
    setReportNumber(e.target.value);
  };

  const onExportTypeChange = (e: any) => {
    const newValue = e.target.value;
    setExportTypeValue(newValue);
  };

  const renderDownloadReportAction = () => {
    const validReportNumber = !!reportNumber.replace(/\s+/g, ""); // only checks that there are characters other than spaces.
    const invalidNvcrExportType =
      exportTypeValue === exportTypeDescriptions[1] &&
      !speciesSummaryChecked &&
      !zoneSummaryChecked;
    if (exportError)
      return (
        <DownloadErrorDiv>
          There was an issue while preparing the data export.{" "}
          <TryAgainButton onClick={requestExport}>Try again</TryAgainButton>
        </DownloadErrorDiv>
      );
    return (
      <FooterButton
        onClick={requestExport}
        disabled={
          preparingExport || !validReportNumber || invalidNvcrExportType
        }
      >
        <span>{preparingExport ? "Preparing" : "Download"}</span>
        {preparingExport && (
          <SpinnerBase
            style={spinnerStyle}
            childrenStyle={spinnerChildrenStyle}
          />
        )}
      </FooterButton>
    );
  };

  return (
    <Container>
      <SubHeader>
        <SubHeaderInnerContainer>
          <SubHeaderLinkContainer>
            <SubHeaderLinkInnerContainer>
              <StyledBackArrow />
              <BackLink to="/">NVR Map</BackLink>
            </SubHeaderLinkInnerContainer>
          </SubHeaderLinkContainer>
          <SubHeaderText>Export data from a previous report</SubHeaderText>
        </SubHeaderInnerContainer>
      </SubHeader>
      <MainContent>
        <MainContentInner>
          <FormField>
            <FormLabel htmlFor="report-number-input">Report number</FormLabel>
            <StyledInput
              id="report-number-input"
              autoFocus={true}
              type="text"
              placeholder="Enter report number"
              value={reportNumber}
              onChange={onReportNumberChange}
            ></StyledInput>
          </FormField>
          <FormField>
            <StyledDropdown
              value={exportTypeValue}
              onChange={onExportTypeChange}
            >
              {exportTypeDescriptions.map((x: any, index) => (
                <option key={index} value={x}>
                  {x}
                </option>
              ))}
            </StyledDropdown>
          </FormField>
          {exportTypeValue === exportTypeDescriptions[1] && (
            <FormField>
              <StyledCheckboxContainer>
                <StyledCheckbox
                  type="checkbox"
                  id="checkbox-species"
                  checked={speciesSummaryChecked}
                  onChange={(e: any) =>
                    setSpeciesSummaryChecked(e.target.checked)
                  }
                />
                <CheckboxLabel htmlFor="checkbox-species">
                  Species summary
                </CheckboxLabel>
              </StyledCheckboxContainer>
              <StyledCheckboxContainer>
                <StyledCheckbox
                  type="checkbox"
                  id="checkbox-zone"
                  checked={zoneSummaryChecked}
                  onChange={(e: any) => setZoneSummaryChecked(e.target.checked)}
                />
                <CheckboxLabel htmlFor="checkbox-zone">
                  Zone summary
                </CheckboxLabel>
              </StyledCheckboxContainer>
            </FormField>
          )}
          <FooterButtonContainer>
            {renderDownloadReportAction()}
          </FooterButtonContainer>
        </MainContentInner>
      </MainContent>
      <Footer />
    </Container>
  );
}

export default Admin;
